/**
 * SampleTrendsView — shared component for Influent and Effluent trend pages.
 * Loads samples of a given type, lets the user pick ≥2, shows regulatory
 * trend charts, stats table and a "Generate Trend Report" button.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  CheckSquare, Square, Search, X, Download, Eye, FileText,
  Building2, Calendar, Layers, TrendingUp, BarChart3, RefreshCw,
  ChevronDown, ChevronUp, Shield, Hash, AlertTriangle,
  ArrowRight, CheckCircle2, Info, Beaker,
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { getAllLocalSamples, getLocalSites } from '@/lib/hybridStorage';
import { hasPhpBackend, apiListInfluentSamples } from '@/lib/phpApi';
import { generateSampleTrendReportPDF, buildRegulatoryReportNumber } from '@/lib/pdfGenerator';
import { ALL_STANDARDS } from '@/types/standards';
import PageHeader from '@/components/PageHeader';

/* ─── PDF Preview Modal ──────────────────────────────────────────────────── */
function PDFPreviewModal({ url, title, onClose, onDownload }: {
  url: string; title: string; onClose: () => void; onDownload: () => void;
}) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-rb-surface rounded-2xl w-full max-w-5xl flex flex-col shadow-2xl overflow-hidden"
        style={{ height: '92vh' }}
      >
        <div className="flex items-center justify-between px-4 py-3 bg-rb-surface border-b border-rb-brand/20 shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="w-4 h-4 text-rb-brand" />
            <span className="text-sm font-bold text-white truncate max-w-md">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-rb-brand text-slate-200 hover:bg-yellow-400 transition-colors">
              <Download className="w-3.5 h-3.5" /> Download
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <iframe src={url} className="flex-1 w-full bg-rb-surface" title="PDF Preview" style={{ border: 'none' }} />
      </motion.div>
    </motion.div>
  );
}

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface SampleRow {
  id: number;
  sampleName: string;
  reportNumber: string;
  createdAt: string;
  params: Record<string, number>;
  standard: string;
  siteId?: string | null;
  sampleType: string;
}

interface Props {
  sampleType: 'influent' | 'effluent';
  title: string;
  subtitle: string;
  accentColor?: string; // tailwind classes
}

/* ─── Param display config ───────────────────────────────────────────────── */
const PARAM_CFG = [
  { key: 'bod5',           label: 'BOD₅',    unit: 'mg/L', color: '#22d3ee', limitKey: 'bod5' },
  { key: 'cod',            label: 'COD',     unit: 'mg/L', color: 'var(--rb-brand)', limitKey: 'cod' },
  { key: 'tss',            label: 'TSS',     unit: 'mg/L', color: 'var(--rb-text-2)', limitKey: 'tss' },
  { key: 'oilGrease',      label: 'O&G',     unit: 'mg/L', color: '#f97316', limitKey: 'oilGrease' },
  { key: 'ph',             label: 'pH',      unit: '—',    color: '#a78bfa', limitKey: 'ph' },
  { key: 'tkn',            label: 'TKN',     unit: 'mg/L', color: '#34d399', limitKey: 'totalNitrogen' },
  { key: 'totalPhosphorus',label: 'TP',      unit: 'mg/L', color: '#f472b6', limitKey: 'totalPhosphorus' },
  { key: 'tds',            label: 'TDS',     unit: 'mg/L', color: 'var(--rb-text-3)', limitKey: null },
  { key: 'ammoniaN',       label: 'NH₃-N',   unit: 'mg/L', color: '#4ade80', limitKey: 'ammoniaN' },
];

const fmtDate = (d: string) => {
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return d; }
};
const fmtDateLong = (d: string) => {
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return d; }
};
const fmtDateTime = (d: string) => {
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' ' + dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  catch { return d; }
};

/* ─── Component ──────────────────────────────────────────────────────────── */
export default function SampleTrendsView({ sampleType, title, subtitle }: Props) {
  const navigate = useNavigate();
  const { currentCustomer, activeStandard, userProfile } = useSession();
  const { isAuthenticated, user } = useAuth();

  const [allSamples, setAllSamples] = useState<SampleRow[]>([]);
  const [selected, setSelected]     = useState<Set<number>>(new Set());
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [pickerOpen, setPickerOpen] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  /** Site filter: only allow trend selection for samples from the same site */
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  const resolveDbCustId = (c: any): number =>
    c ? (c.serverId ?? c.dbId ?? c.id ?? 0) : 0;

  const dbCustId = resolveDbCustId(currentCustomer);
  const stdCode  = (currentCustomer as any)?.standard || activeStandard || 'UK_EA';
  const std      = ALL_STANDARDS.find(s => s.code === stdCode) || null;

  /* ── Load samples ── */
  const loadSamples = useCallback(async () => {
    setLoading(true);
    try {
      // Local samples
      const local = getAllLocalSamples()
        .filter(s => s.sampleType === sampleType)
        .filter(s => !dbCustId || !s.customerId || Number(s.customerId) === dbCustId)
        .map(s => ({
          id: Number(s.id),
          sampleName: s.sampleName,
          reportNumber: s.reportNumber || `RB-${sampleType.toUpperCase()}-${s.id}`,
          createdAt: s.createdAt,
          params: s.params || {},
          standard: s.standard || stdCode,
          siteId: s.siteId,
          sampleType: s.sampleType || sampleType,
        }));

      if (isAuthenticated && hasPhpBackend() && dbCustId > 0) {
        try {
          const apiRows = await apiListInfluentSamples(dbCustId, { type: sampleType });
          const apiSamples: SampleRow[] = (apiRows || []).map((r: any) => ({
            id: Number(r.id),
            sampleName: r.sampleName || r.sample_name || 'Untitled',
            reportNumber: r.reportNumber || r.report_number || `RB-${r.id}`,
            createdAt: r.createdAt || r.created_at || new Date().toISOString(),
            params: typeof r.params === 'string' ? JSON.parse(r.params) : (r.params || {}),
            standard: r.standard || stdCode,
            siteId: r.siteId ?? r.site_id ?? null,
            sampleType: sampleType,
          }));
          // Merge, DB takes precedence
          const dbIds = new Set(apiSamples.map(s => s.id));
          const localOnly = local.filter(s => !dbIds.has(s.id));
          setAllSamples([...apiSamples, ...localOnly].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ));
          return;
        } catch { /* fall through to local */ }
      }

      setAllSamples(local.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ));
    } finally {
      setLoading(false);
    }
  }, [sampleType, dbCustId, isAuthenticated, stdCode]);

  useEffect(() => { loadSamples(); }, [loadSamples]);

  /* ── Unique sites derived from loaded samples, names resolved from local store ── */
  const siteOptions = useMemo(() => {
    const localSites = getLocalSites();
    const seen = new Map<string, string>();
    allSamples.forEach(s => {
      const sid = s.siteId ? String(s.siteId) : null;
      if (sid && !seen.has(sid)) {
        const localSite = localSites.find(ls => String(ls.id) === sid);
        seen.set(sid, localSite?.name || `Site ${sid}`);
      }
    });
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [allSamples]);

  /* ── Filtered picker list — site-scoped for regulatory compliance ── */
  const pickerSamples = useMemo(() => {
    let rows = allSamples;
    // Site scope: only show samples from the selected site
    if (selectedSiteId) rows = rows.filter(s => s.siteId ? String(s.siteId) === selectedSiteId : false);
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(s =>
      s.sampleName.toLowerCase().includes(q) ||
      s.reportNumber.toLowerCase().includes(q) ||
      fmtDateLong(s.createdAt).toLowerCase().includes(q),
    );
  }, [allSamples, search, selectedSiteId]);

  /* ── Selected samples sorted by date ── */
  const selectedSamples = useMemo(() =>
    allSamples
      .filter(s => selected.has(s.id))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
  [allSamples, selected]);

  /* ── 5a: Linked sample pairs (effluent → influent) ── */
  const linkedPairs = useMemo(() => {
    if (sampleType !== 'effluent') return [];
    return selectedSamples
      .filter(s => (s as any).linkedSampleId)
      .map(eff => {
        const inf = allSamples.find(i => i.id === (eff as any).linkedSampleId)
          || getAllLocalSamples().find(i => Number(i.id) === (eff as any).linkedSampleId);
        return inf ? { effluent: eff, influent: inf } : null;
      })
      .filter(Boolean) as { effluent: SampleRow; influent: any }[];
  }, [selectedSamples, allSamples, sampleType]);

  /* ── 5a: Linked effluent for influent samples ── */
  const linkedEffluents = useMemo(() => {
    if (sampleType !== 'influent') return [];
    const effluentSamples = getAllLocalSamples().filter(s => s.sampleType === 'effluent');
    return selectedSamples
      .map(inf => {
        const eff = effluentSamples.find(e => Number(e.linkedSampleId) === inf.id);
        return eff ? { influent: inf, effluent: eff } : null;
      })
      .filter(Boolean) as { influent: SampleRow; effluent: any }[];
  }, [selectedSamples, sampleType]);

  /* ── 5b: DAF end-of-process-train detection ── */
  const isDAFAtEndOfTrain = useMemo(() => {
    try {
      const steps = JSON.parse(localStorage.getItem('rb_process_train_steps') || '[]');
      if (!steps.length) return false;
      const last = steps[steps.length - 1];
      return last?.type === 'daf' || last?.id === 'daf' || String(last?.name || '').toLowerCase().includes('daf');
    } catch { return false; }
  }, []);

  /* ── Chart data ── */
  const chartData = useMemo(() =>
    selectedSamples.map(s => ({
      date: fmtDate(s.createdAt),
      dateISO: s.createdAt,
      name: s.sampleName,
      ...PARAM_CFG.reduce((acc, p) => {
        acc[p.key] = s.params[p.key] ?? s.params[p.key.replace('5', '').toLowerCase()] ?? 0;
        return acc;
      }, {} as Record<string, number>),
    })),
  [selectedSamples]);

  /* ── Stats ── */
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    return PARAM_CFG.map(p => {
      const vals = chartData.map(d => d[p.key]).filter(v => v > 0);
      if (vals.length === 0) return null;
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      const max = Math.max(...vals);
      const limit = std && p.limitKey ? (std.limits as any)[p.limitKey] : null;
      const pass  = limit != null ? avg <= limit : null;
      return { ...p, avg, max, limit, pass, count: vals.length };
    }).filter(Boolean);
  }, [chartData, std]);

  /* ── Toggle selection ── */
  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll   = () => setSelected(new Set(pickerSamples.map(s => s.id)));
  const clearAll    = () => setSelected(new Set());

  /* ── PDF helpers ── */
  const buildPDFArgs = () => {
    const reportNum = buildRegulatoryReportNumber(sampleType === 'influent' ? 'INF' : 'EFF', stdCode);
    const custName  = (currentCustomer as any)?.company || (currentCustomer as any)?.name || '';
    const pdfSamples = selectedSamples.map(s => ({
      name: s.sampleName,
      referenceId: s.reportNumber,
      date: new Date(s.createdAt),
      params: s.params,
    }));
    const pdfOpts = {
      reportNumber: reportNum,
      reportTitle: `${sampleType === 'influent' ? 'Influent' : 'Effluent'} Sample Trend Analysis — ${custName}`,
      customerName: custName || undefined,
      standard: stdCode,
      createdAt: new Date(),
    };
    return { pdfSamples, pdfOpts, reportNum };
  };

  const handleDownloadPDF = () => {
    if (selectedSamples.length < 2) { toast.error('Select at least 2 samples to generate a report'); return; }
    try {
      const { pdfSamples, pdfOpts } = buildPDFArgs();
      generateSampleTrendReportPDF(pdfSamples, pdfOpts, 'save');
      toast.success('Regulatory PDF downloaded');
    } catch (err: any) {
      toast.error(`PDF failed: ${err.message}`);
    }
  };

  const handlePreview = () => {
    if (selectedSamples.length < 2) { toast.error('Select at least 2 samples to preview'); return; }
    try {
      const { pdfSamples, pdfOpts, reportNum } = buildPDFArgs();
      const url = generateSampleTrendReportPDF(pdfSamples, pdfOpts, 'preview');
      if (!url) { toast.error('Preview failed'); return; }
      setPreviewUrl(url);
      setPreviewTitle(`${reportNum} — ${sampleType === 'influent' ? 'Influent' : 'Effluent'} Trend Report`);
    } catch (err: any) {
      toast.error(`Preview failed: ${err.message}`);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const canAnalyse = selectedSamples.length >= 2;

  return (
    <div className="space-y-4">
      {/* Preview modal */}
      <AnimatePresence>
        {previewUrl && (
          <PDFPreviewModal
            url={previewUrl}
            title={previewTitle}
            onClose={closePreview}
            onDownload={() => {
              const { pdfSamples, pdfOpts } = buildPDFArgs();
              generateSampleTrendReportPDF(pdfSamples, pdfOpts, 'save');
              toast.success('PDF downloaded');
            }}
          />
        )}
      </AnimatePresence>

      <PageHeader
        icon={TrendingUp}
        title={title}
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <span className="text-xs font-bold text-slate-300 px-2.5 py-1.5 rounded-lg bg-slate-800/80 border border-slate-800/60">
                {selected.size} selected
              </span>
            )}
            <button onClick={loadSamples} className="p-2 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 border border-transparent hover:border-slate-800/60 transition-all" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        }
      />

      {/* ── No customer guard ── */}
      {!currentCustomer && (
        <div className="rb-card p-10 text-center border border-amber-500/20 bg-amber-500/5">
          <AlertTriangle className="w-8 h-8 text-amber-400/50 mx-auto mb-3" />
          <p className="text-sm font-semibold text-amber-400">No active customer</p>
          <p className="text-xs text-slate-500 mt-1">Select a customer from the Customers page to view samples.</p>
        </div>
      )}

      {currentCustomer && (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">

          {/* ═══ LEFT: Sample Picker ═══ */}
          <div className="space-y-3">
            {/* Picker header */}
            <div
              className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-800/60 border border-slate-800/50 cursor-pointer hover:bg-slate-800 transition-all"
              onClick={() => setPickerOpen(v => !v)}
            >
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-rb-brand" />
                <span className="text-xs font-bold text-white">
                  Available Samples
                  {allSamples.length > 0 && (
                    <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-400">
                      {allSamples.length}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {selected.size > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rb-brand/20 text-rb-brand font-bold">
                    {selected.size} ✓
                  </span>
                )}
                {pickerOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
              </div>
            </div>

            <AnimatePresence>
              {pickerOpen && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }} className="overflow-hidden"
                >
                  <div className="space-y-2">
                    {/* Site filter — regulatory requirement: trends within same site only */}
                    {siteOptions.length > 0 && (
                      <div>
                        <p className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1">
                          <Hash className="w-2.5 h-2.5" /> Site Filter <span className="text-amber-400">(required for regulatory comparison)</span>
                        </p>
                        <select
                          value={selectedSiteId ?? ''}
                          onChange={e => { setSelectedSiteId(e.target.value || null); setSelected(new Set()); }}
                          className="w-full px-3 py-2 rounded-xl bg-slate-800/60 border border-slate-800/50 text-xs text-white focus:outline-none focus:border-rb-brand/40"
                        >
                          <option value="">All sites (not recommended for trends)</option>
                          {siteOptions.map(s => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                      <input
                        type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search samples…"
                        className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-800/60 border border-slate-800/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-rb-brand/40"
                      />
                      {search && (
                        <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {/* Select all / clear */}
                    <div className="flex gap-2">
                      <button onClick={selectAll} className="flex-1 py-1.5 rounded-lg text-[11px] font-medium bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition-all">
                        Select All ({pickerSamples.length})
                      </button>
                      <button onClick={clearAll} className="flex-1 py-1.5 rounded-lg text-[11px] font-medium bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700 transition-all">
                        Clear
                      </button>
                    </div>

                    {/* Sample list */}
                    {loading ? (
                      <div className="space-y-1.5">
                        {[1,2,3].map(i => <div key={i} className="h-12 rounded-xl bg-slate-800/60 animate-pulse" />)}
                      </div>
                    ) : pickerSamples.length === 0 ? (
                      <div className="text-center py-8 text-xs text-slate-500">
                        No {sampleType} samples found for this customer
                      </div>
                    ) : (
                      <div className="space-y-1 max-h-[280px] sm:max-h-[360px] lg:max-h-[420px] overflow-y-auto pr-1">
                        {pickerSamples.map(s => {
                          const isSelected = selected.has(s.id);
                          return (
                            <button
                              key={s.id}
                              onClick={() => toggle(s.id)}
                              className={`w-full flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all ${
                                isSelected
                                  ? 'bg-rb-active/80 border-rb-brand/30 shadow-md'
                                  : 'bg-slate-800/40 border-slate-800/40 hover:border-slate-600/60 hover:bg-slate-800/60'
                              }`}
                            >
                              <div className="mt-0.5 shrink-0 text-rb-brand">
                                {isSelected
                                  ? <CheckSquare className="w-3.5 h-3.5" />
                                  : <Square className="w-3.5 h-3.5 text-slate-600" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-semibold truncate ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                                  {s.sampleName}
                                </p>
                                <p className={`text-[10px] font-mono font-bold truncate ${isSelected ? 'text-rb-brand' : 'text-slate-500'}`}>
                                  {s.reportNumber}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[9px] text-slate-500 flex items-center gap-1">
                                    <Calendar className="w-2.5 h-2.5" />
                                    {fmtDateTime(s.createdAt)}
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Minimum requirement hint */}
                    {selected.size === 1 && (
                      <p className="text-center text-[10px] text-amber-400 py-1">
                        Select at least 1 more sample to enable trend analysis
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Action buttons ── */}
            {canAnalyse && (
              <div className="space-y-2 pt-1">
                {/* Mobile: prompt to scroll for charts */}
                <p className="lg:hidden text-center text-[10px] text-rb-brand flex items-center justify-center gap-1">
                  <ChevronDown className="w-3 h-3" /> Scroll down to view trend charts
                </p>
                <button
                  onClick={handlePreview}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 transition-all"
                >
                  <Eye className="w-3.5 h-3.5 text-rb-brand" /> Preview Regulatory PDF
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold bg-rb-active text-white border border-rb-brand/30 hover:bg-rb-surface transition-all"
                >
                  <Download className="w-3.5 h-3.5 text-rb-brand" /> Download Regulatory PDF
                </button>
              </div>
            )}
          </div>

          {/* ═══ RIGHT: Charts & Analysis ═══ */}
          <div className="space-y-4">
            {!canAnalyse ? (
              <div className="rb-card p-12 text-center border border-slate-800/40">
                <BarChart3 className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-sm font-semibold text-slate-400 mb-1">Select samples to view trends</p>
                <p className="text-xs text-slate-600">
                  Choose 2 or more {sampleType} samples from the list to display trend analysis
                </p>
              </div>
            ) : (
              <>
                {/* Standard badge */}
                {std && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rb-active/40 border border-rb-brand/15">
                    <Shield className="w-3.5 h-3.5 text-rb-brand" />
                    <span className="text-xs font-bold text-rb-brand">{std.flag} {std.shortName}</span>
                    <span className="text-[10px] text-slate-500">— {std.name}</span>
                    <span className="ml-auto text-[10px] text-slate-500">{selectedSamples.length} samples selected</span>
                  </div>
                )}

                {/* Stats grid */}
                {stats && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {stats.slice(0, 4).map((s: any) => (
                      <div key={s.key} className={`p-2.5 rounded-xl border ${
                        s.pass === false ? 'border-red-500/20 bg-red-500/5' :
                        s.pass === true  ? 'border-emerald-500/20 bg-emerald-500/5' :
                        'border-slate-800/40 bg-slate-800/30'
                      }`}>
                        <div className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">{s.label}</div>
                        <div className={`text-base font-bold mt-0.5 ${
                          s.pass === false ? 'text-red-400' : s.pass === true ? 'text-emerald-400' : 'text-white'
                        }`} style={{ color: s.pass == null ? s.color : undefined }}>
                          {s.avg.toFixed(s.key === 'ph' ? 2 : 1)}
                        </div>
                        <div className="text-[9px] text-slate-600 mt-0.5">{s.unit}</div>
                        {s.limit != null && (
                          <div className={`text-[9px] font-bold mt-0.5 ${s.pass ? 'text-emerald-400' : 'text-red-400'}`}>
                            Limit: {s.limit} {s.pass ? '✓' : '✗'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* ── 5c: Summary Findings panel ── */}
                {stats && (() => {
                  const withLimits = stats.filter((s: any) => s.limit != null);
                  const failing = withLimits.filter((s: any) => s.pass === false);
                  const passing = withLimits.filter((s: any) => s.pass === true);
                  const noLimits = withLimits.length === 0;

                  // Trend direction: compare avg of first half vs second half of selected samples
                  const keyParams = ['bod5', 'cod', 'tss'];
                  let trendDir: 'improving' | 'worsening' | 'stable' = 'stable';
                  if (chartData.length >= 4) {
                    const half = Math.floor(chartData.length / 2);
                    const firstHalf = chartData.slice(0, half);
                    const secondHalf = chartData.slice(half);
                    const avgFirst = keyParams.reduce((sum, k) => sum + (firstHalf.reduce((s, d) => s + (d[k] || 0), 0) / firstHalf.length), 0) / keyParams.length;
                    const avgSecond = keyParams.reduce((sum, k) => sum + (secondHalf.reduce((s, d) => s + (d[k] || 0), 0) / secondHalf.length), 0) / keyParams.length;
                    if (avgSecond < avgFirst * 0.9) trendDir = 'improving';
                    else if (avgSecond > avgFirst * 1.1) trendDir = 'worsening';
                  }

                  // Anomaly detection: any param >3x average
                  const anomalyParams: string[] = [];
                  stats.forEach((s: any) => {
                    const vals = chartData.map((d: any) => d[s.key]).filter((v: number) => v > 0);
                    if (vals.length < 2) return;
                    const avg = vals.reduce((a: number, b: number) => a + b, 0) / vals.length;
                    if (vals.some((v: number) => v > avg * 3)) anomalyParams.push(s.label);
                  });

                  // Recommended next steps for effluent failures
                  const recs: string[] = [];
                  if (failing.some((s: any) => ['bod5', 'cod'].includes(s.key))) recs.push('High BOD/COD — consider secondary biological treatment');
                  if (failing.some((s: any) => s.key === 'tss')) recs.push('High TSS — review DAF performance, check coagulant dose');
                  if (failing.some((s: any) => s.key === 'totalPhosphorus')) recs.push('High TP — add chemical phosphorus dosing');
                  if (failing.some((s: any) => s.key === 'ammoniaN')) recs.push('High NH₃-N — add nitrification step (MBR or SBR)');

                  const statusColor = noLimits
                    ? 'border-slate-600/30 bg-slate-800/20'
                    : failing.length === 0
                      ? 'border-emerald-500/25 bg-emerald-500/5'
                      : failing.length <= 2
                        ? 'border-amber-500/25 bg-amber-500/5'
                        : 'border-red-500/25 bg-red-500/5';

                  return (
                    <div className={`rb-card p-4 border ${statusColor}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="w-3.5 h-3.5 text-rb-brand" />
                        <h3 className="text-xs font-bold text-white uppercase tracking-wider">Summary Findings</h3>
                        {!noLimits && (
                          <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            failing.length === 0 ? 'bg-emerald-500/15 text-emerald-400' :
                            failing.length <= 2  ? 'bg-amber-500/15 text-amber-400' :
                            'bg-red-500/15 text-red-400'
                          }`}>
                            {failing.length === 0 ? 'ALL PASS' : `${failing.length} FAIL`}
                          </span>
                        )}
                      </div>

                      {/* Status summary */}
                      {noLimits ? (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                          <Info className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          <p className="text-xs text-slate-500">Reference data — no discharge limits applied for this standard</p>
                        </div>
                      ) : failing.length === 0 ? (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/8 border border-emerald-500/20">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          <p className="text-xs text-emerald-400 font-semibold">Compliant — all assessed parameters within discharge limits</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/8 border border-red-500/20">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs text-red-400 font-semibold">Exceeding limits: {failing.map((s: any) => s.label).join(', ')}</p>
                              <p className="text-[10px] text-slate-500 mt-0.5">{passing.length} of {withLimits.length} assessed parameters within limits</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Trend direction */}
                      {chartData.length >= 4 && (
                        <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-500">
                          <TrendingUp className="w-3 h-3" />
                          Trend: <span className={
                            trendDir === 'improving' ? 'text-emerald-400 font-bold' :
                            trendDir === 'worsening' ? 'text-red-400 font-bold' :
                            'text-slate-400'
                          }>{trendDir === 'improving' ? 'Improving (key params decreasing)' : trendDir === 'worsening' ? 'Worsening (key params increasing)' : 'Stable'}</span>
                        </div>
                      )}

                      {/* Anomaly warning */}
                      {anomalyParams.length > 0 && (
                        <div className="mt-2 flex items-center gap-2 text-[10px] text-amber-400">
                          <AlertTriangle className="w-3 h-3 shrink-0" />
                          Spike detected in: {anomalyParams.join(', ')} (&gt;3× average in at least one sample)
                        </div>
                      )}

                      {/* Effluent-specific section */}
                      {sampleType === 'effluent' && (
                        <div className="mt-3 space-y-2">
                          {isDAFAtEndOfTrain && (
                            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-blue-500/8 border border-blue-500/20">
                              <Info className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                              <p className="text-[10px] text-blue-300">
                                DAF is the final treatment stage. This effluent data represents the final discharge quality.
                                If any parameters exceed licence limits, this data should be used in the Effluent Records page for regulatory reporting.
                              </p>
                            </div>
                          )}
                          {recs.length > 0 && (
                            <div className="px-3 py-2.5 rounded-lg bg-slate-800/40 border border-slate-800/30">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Recommended next steps</p>
                              <ul className="space-y-1">
                                {recs.map((r, i) => (
                                  <li key={i} className="text-[10px] text-slate-400 flex items-start gap-1.5">
                                    <span className="text-rb-brand mt-0.5">›</span> {r}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <button
                            onClick={() => navigate('/effluent')}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 transition-all"
                          >
                            <Beaker className="w-3 h-3" /> Open Effluent Records <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      )}

                      {/* Influent-specific section */}
                      {sampleType === 'influent' && (
                        <div className="mt-3 space-y-2">
                          <div className="px-3 py-2.5 rounded-lg bg-slate-800/40 border border-slate-800/30">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Influent Quality Profile</p>
                            <p className="text-[10px] text-slate-500">
                              {selectedSamples.length} influent sample{selectedSamples.length !== 1 ? 's' : ''} selected.
                              {linkedEffluents.length > 0 && ` ${linkedEffluents.length} linked effluent sample${linkedEffluents.length !== 1 ? 's' : ''} found — paired comparison available below.`}
                            </p>
                          </div>
                          <button
                            onClick={() => navigate('/daf-spec')}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-rb-active text-white border border-rb-brand/20 hover:bg-rb-surface transition-all"
                          >
                            Run DAF Assessment <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      )}

                      {/* Reference IDs list */}
                      {selectedSamples.length > 0 && (
                        <div className="mt-3">
                          <p className="text-[9px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Sample Reference IDs</p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedSamples.map(s => (
                              <span key={s.id} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-800/60 border border-slate-800/40 text-rb-brand">
                                {s.reportNumber}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── 5d: Linked Sample Pairs display ── */}
                {(linkedPairs.length > 0 || linkedEffluents.length > 0) && (() => {
                  const pairs = linkedPairs.length > 0 ? linkedPairs : linkedEffluents.map(le => ({ effluent: le.effluent, influent: le.influent }));
                  return (
                    <div className="rb-card overflow-hidden">
                      <div className="px-4 py-2.5 border-b border-slate-800/50 bg-slate-800/30 flex items-center gap-2">
                        <Hash className="w-3.5 h-3.5 text-rb-brand" />
                        <h3 className="text-xs font-bold text-slate-300">Linked Sample Pairs</h3>
                        <span className="ml-auto text-[10px] text-slate-600">{pairs.length} pair{pairs.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="bg-slate-800/60 border-b border-slate-700">
                              <th className="py-2 px-3 text-left font-bold text-slate-400 text-[10px] uppercase tracking-wider">Date</th>
                              <th className="py-2 px-3 text-left font-bold text-slate-400 text-[10px] uppercase tracking-wider">Influent Ref</th>
                              <th className="py-2 px-3 text-left font-bold text-slate-400 text-[10px] uppercase tracking-wider">Effluent Ref</th>
                              <th className="py-2 px-2 text-right font-bold text-slate-400 text-[10px] uppercase tracking-wider">BOD rem%</th>
                              <th className="py-2 px-2 text-right font-bold text-slate-400 text-[10px] uppercase tracking-wider">COD rem%</th>
                              <th className="py-2 px-2 text-right font-bold text-slate-400 text-[10px] uppercase tracking-wider">TSS rem%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pairs.map((pair, i) => {
                              const infP = pair.influent.params || {};
                              const effP = pair.effluent.params || {};
                              const remPct = (inf: number, eff: number) => {
                                if (!inf || inf === 0) return '—';
                                const pct = ((inf - eff) / inf) * 100;
                                return `${pct.toFixed(0)}%`;
                              };
                              const bodRem = remPct(infP.bod5 || infP.bod || 0, effP.bod5 || effP.bod || 0);
                              const codRem = remPct(infP.cod || 0, effP.cod || 0);
                              const tssRem = remPct(infP.tss || 0, effP.tss || 0);
                              return (
                                <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                                  <td className="py-2 px-3 text-slate-400 whitespace-nowrap">{fmtDate(pair.influent.createdAt || '')}</td>
                                  <td className="py-2 px-3 font-mono text-[10px] text-rb-brand truncate max-w-[120px]">{pair.influent.reportNumber || '—'}</td>
                                  <td className="py-2 px-3 font-mono text-[10px] text-sky-400 truncate max-w-[120px]">{pair.effluent.reportNumber || '—'}</td>
                                  <td className="py-2 px-2 text-right font-mono text-slate-300">{bodRem}</td>
                                  <td className="py-2 px-2 text-right font-mono text-slate-300">{codRem}</td>
                                  <td className="py-2 px-2 text-right font-mono text-slate-300">{tssRem}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* BOD/COD/TSS trend chart */}
                <div className="rb-card p-4">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">
                    Organic Loading Trend — BOD · COD · TSS (mg/L)
                  </h3>
                  <div className="h-44 sm:h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 8, left: 8, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="date" tick={{ fill: 'var(--rb-text-3)', fontSize: 8 }} stroke="#334155" />
                        <YAxis tick={{ fill: 'var(--rb-text-3)', fontSize: 8 }} stroke="#334155" width={36} />
                        <ReTooltip contentStyle={{ backgroundColor: 'var(--rb-elevated)', borderColor: '#334155', borderRadius: '0.5rem', color: 'var(--rb-text)', fontSize: 'var(--rb-font-xs-)' }} />
                        <Legend wrapperStyle={{ fontSize: 'var(--rb-font-xxs)', color: 'var(--rb-text-2)' }} />
                        {std && <ReferenceLine yAxisId={undefined} y={std.limits.bod5} stroke="#22d3ee" strokeDasharray="4 2" label={{ value: `BOD lim`, fill: '#22d3ee', fontSize: 8 }} />}
                        {std && <ReferenceLine y={std.limits.cod}  stroke="var(--rb-brand)" strokeDasharray="4 2" label={{ value: `COD lim`, fill: 'var(--rb-brand)', fontSize: 8 }} />}
                        {std && <ReferenceLine y={std.limits.tss}  stroke="#94a3b8" strokeDasharray="4 2" label={{ value: `TSS lim`, fill: 'var(--rb-text-2)', fontSize: 8 }} />}
                        <Line type="monotone" dataKey="bod5"  name="BOD₅" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3, fill: '#22d3ee' }} />
                        <Line type="monotone" dataKey="cod"   name="COD"  stroke="var(--rb-brand)" strokeWidth={2} dot={{ r: 3, fill: 'var(--rb-brand)' }} />
                        <Line type="monotone" dataKey="tss"   name="TSS"  stroke="#94a3b8" strokeWidth={2} dot={{ r: 3, fill: 'var(--rb-text-2)' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* O&G + pH chart */}
                <div className="rb-card p-4">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">
                    O&G, pH &amp; Nutrients Trend
                  </h3>
                  <div className="h-44 sm:h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 8, left: 8, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="date" tick={{ fill: 'var(--rb-text-3)', fontSize: 8 }} stroke="#334155" />
                        <YAxis yAxisId="left"  tick={{ fill: 'var(--rb-text-3)', fontSize: 8 }} stroke="#334155" width={36} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fill: 'var(--rb-text-3)', fontSize: 8 }} stroke="#334155" domain={[0, 14]} width={30} />
                        <ReTooltip contentStyle={{ backgroundColor: 'var(--rb-elevated)', borderColor: '#334155', borderRadius: '0.5rem', color: 'var(--rb-text)', fontSize: 'var(--rb-font-xs-)' }} />
                        <Legend wrapperStyle={{ fontSize: 'var(--rb-font-xxs)', color: 'var(--rb-text-2)' }} />
                        {std && <ReferenceLine yAxisId="left" y={std.limits.oilGrease} stroke="#f97316" strokeDasharray="4 2" />}
                        <Line yAxisId="left"  type="monotone" dataKey="oilGrease"       name="O&G"   stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
                        <Line yAxisId="left"  type="monotone" dataKey="tkn"             name="TKN"   stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} />
                        <Line yAxisId="right" type="monotone" dataKey="ph"              name="pH"    stroke="#a78bfa" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Data table */}
                <div className="rb-card overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-slate-800/50 bg-slate-800/30 flex items-center gap-2">
                    <BarChart3 className="w-3.5 h-3.5 text-slate-500" />
                    <h3 className="text-xs font-bold text-slate-300">Sample Data Table</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="bg-slate-800/60 border-b border-slate-700">
                          <th className="py-2 px-3 text-left font-bold text-slate-400 text-[10px] uppercase tracking-wider">Sample</th>
                          <th className="py-2 px-3 text-left font-bold text-slate-400 text-[10px] uppercase tracking-wider">Date</th>
                          {PARAM_CFG.slice(0, 7).map(p => (
                            <th key={p.key} className="py-2 px-2 text-right font-bold text-slate-400 text-[10px] uppercase tracking-wider whitespace-nowrap">
                              {p.label}
                            </th>
                          ))}
                        </tr>
                        {std && (
                          <tr className="border-b border-slate-800/50 bg-slate-800/20">
                            <td className="py-1 px-3 text-[9px] text-slate-600 font-bold" colSpan={2}>Discharge Limits</td>
                            {PARAM_CFG.slice(0, 7).map(p => (
                              <td key={p.key} className="py-1 px-2 text-right text-[9px] text-rb-brand font-mono">
                                {p.limitKey ? (std.limits as any)[p.limitKey] ?? '—' : '—'}
                              </td>
                            ))}
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {selectedSamples.map((s, i) => (
                          <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                            <td className="py-2 px-3 font-semibold text-white truncate max-w-[120px]">{s.sampleName}</td>
                            <td className="py-2 px-3 text-slate-500 whitespace-nowrap">{fmtDateLong(s.createdAt)}</td>
                            {PARAM_CFG.slice(0, 7).map(p => {
                              const val = s.params[p.key] ?? 0;
                              const limit = std && p.limitKey ? (std.limits as any)[p.limitKey] : null;
                              const fail  = limit != null && val > 0 && val > limit;
                              return (
                                <td key={p.key} className={`py-2 px-2 text-right font-mono ${val === 0 ? 'text-slate-400' : fail ? 'text-red-400 font-bold' : 'text-slate-300'}`}>
                                  {val > 0 ? val.toFixed(p.key === 'ph' ? 2 : 1) : '—'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
